"use client";

import {
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
  type Ref,
} from "react";

export type RevealVariant = "up" | "left" | "right" | "zoom" | "fade";

type UseRevealOptions = {
  variant?: RevealVariant;
  delayMs?: number;
  threshold?: number;
  once?: boolean;
};

const variantHidden: Record<RevealVariant, string> = {
  up: "translate-y-8 opacity-0",
  left: "-translate-x-10 opacity-0",
  right: "translate-x-10 opacity-0",
  zoom: "scale-95 opacity-0",
  fade: "opacity-0",
};

export function useReveal({
  variant = "up",
  delayMs = 0,
  threshold = 0.12,
  once = true,
}: UseRevealOptions = {}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Content stays in the DOM for crawlers; skip motion when users prefer reduced motion.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, threshold]);

  const className = [
    "motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out",
    !visible ? "motion-safe:will-change-transform" : undefined,
    visible
      ? "translate-x-0 translate-y-0 scale-100 opacity-100"
      : variantHidden[variant],
  ]
    .filter(Boolean)
    .join(" ");

  const style = delayMs ? { transitionDelay: `${delayMs}ms` } : undefined;

  return { ref, className, style, visible };
}

type RevealProps<T extends ElementType = "div"> = {
  children: ReactNode;
  variant?: RevealVariant;
  delayMs?: number;
  className?: string;
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "children" | "className">;

export function Reveal<T extends ElementType = "div">({
  children,
  variant = "up",
  delayMs = 0,
  className,
  as,
  ...rest
}: RevealProps<T>) {
  const Tag = (as ?? "div") as any;
  const reveal = useReveal({ variant, delayMs });
  const clipsHorizontal = variant === "left" || variant === "right";

  if (clipsHorizontal) {
    return (
      <Tag
        className={["min-w-0 overflow-x-clip", className].filter(Boolean).join(" ")}
        {...rest}
      >
        <div
          ref={reveal.ref as Ref<HTMLDivElement>}
          className={[reveal.className, "h-full w-full"].join(" ")}
          style={reveal.style}
        >
          {children}
        </div>
      </Tag>
    );
  }

  return (
    <Tag
      ref={reveal.ref}
      className={[reveal.className, className].filter(Boolean).join(" ")}
      style={reveal.style}
      {...rest}
    >
      {children}
    </Tag>
  );
}

type RevealHeaderProps = {
  eyebrow?: ReactNode;
  heading: ReactNode;
  description?: ReactNode;
  align?: "left" | "center";
  className?: string;
  headingClassName?: string;
  maxWidthClassName?: string;
};

/** Consistent section header reveal: eyebrow → title → description. */
export function RevealHeader({
  eyebrow,
  heading,
  description,
  align = "center",
  className,
  headingClassName,
  maxWidthClassName = "max-w-3xl",
}: RevealHeaderProps) {
  const alignClass =
    align === "center"
      ? ["mx-auto", maxWidthClassName, "text-center"].join(" ")
      : [maxWidthClassName, "text-left"].join(" ");

  return (
    <div className={[alignClass, className].filter(Boolean).join(" ")}>
      {eyebrow ? <Reveal variant="fade">{eyebrow}</Reveal> : null}
      <Reveal variant="up" delayMs={eyebrow ? 80 : 0}>
        <div className={headingClassName}>{heading}</div>
      </Reveal>
      {description ? (
        <Reveal variant="up" delayMs={160}>
          {description}
        </Reveal>
      ) : null}
    </div>
  );
}
